import {Box} from 'src/design/atoms/Box';
import {Grid} from 'src/design/atoms/Grid';
import React from 'react';
import {Button} from 'src/design/atoms/Button';
import type {IFormDataV2, IFormFieldV2} from 'src/types/FormV2';
import {FormViewField} from '../FormViewField';
import {useFormVisibility} from '../hooks/useFormVisibility';

export interface FormViewComponentV2Props {
  formData: IFormDataV2;
  formMeta: IFormFieldV2[];
  formHeader?: React.ReactNode;
  limitItemsCount?: number;
  spacing?: 'compact' | 'normal';
}

export const FormViewComponentV2: React.FC<FormViewComponentV2Props> =
  React.memo(
    ({formData, formMeta, formHeader, limitItemsCount, spacing = 'normal'}) => {
      const [formMetaLimit, setFormMetaLimit] = React.useState<number>(
        limitItemsCount || formMeta.length
      );
      const [showMore, setShowMore] = React.useState<boolean>(false);

      const visibility = useFormVisibility(formMeta, formData);

      React.useEffect(() => {
        if (limitItemsCount && !showMore) {
          setFormMetaLimit(limitItemsCount);
        } else {
          setFormMetaLimit(formMeta.length);
        }
      }, [limitItemsCount, formMeta.length, showMore]);

      const visibleFields = React.useMemo(
        () =>
          formMeta
            .slice(0, formMetaLimit)
            .filter(field => visibility[field.name]),
        [formMeta, formMetaLimit, visibility]
      );

      return (
        <Box data-testid="form-view-container" mb="xl">
          {formHeader}
          <Grid
            mb={spacing === 'compact' ? 'xs' : 'xl'}
            gutter={spacing === 'compact' ? 'xs' : 'md'}
            maw={{base: '100%', xl: '1200px'}}>
            {visibleFields.map(field => (
              <FormViewField
                key={field.name}
                field={field}
                value={
                  field.type === 'project'
                    ? formData['project']
                    : formData[field.name]
                }
              />
            ))}
          </Grid>
          {limitItemsCount && formMeta.length > limitItemsCount && (
            <Box pt="md">
              <Button size="sm" onClick={() => setShowMore(!showMore)}>
                {showMore ? 'Show Less' : 'Show More'}
              </Button>
            </Box>
          )}
        </Box>
      );
    }
  );
